import React, { useEffect, useState } from "react";
import api, { friendlyMessage } from "../../lib/api";
import { formatINR } from "../../lib/utils.fmt";
import { Building2, Users, Calendar, TrendingUp, Plus, Power, Settings, Truck, Package, Sparkles, Check, AlertTriangle, Wallet, ChevronRight, Crown, Trash2, Flame, RefreshCw, Image as ImageIcon, UploadCloud, CheckCircle2, FileText, Copy, Smartphone, Tablet, Palette, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import EmptyState, { LoadingState } from "../../components/EmptyState";

export function SuperDashboard() {
  const [d, setD] = useState(null);
  const [risk, setRisk] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const loadDashboard = () => {
    setLoadError(null);
    api.get("/api/super-admin/dashboard").then(({ data }) => setD(data))
      .catch((e) => setLoadError(friendlyMessage(e)));
    api.get("/api/super-admin/renewals/at-risk?window_days=30").then(({ data }) => setRisk(data)).catch(() => {});
  };
  useEffect(() => { loadDashboard(); }, []);
  if (loadError && !d) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load the dashboard" description={loadError}
        action={<button onClick={loadDashboard} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }
  if (!d) return <LoadingState fullHeight />;
  return (
    <div className="space-y-8 page-enter" data-testid="super-dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-brand-clay">Platform Owner</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">DairyFresh SaaS Console</h1>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Kpi icon={Building2} label="Total Tenants" value={d.total_tenants}/>
        <Kpi icon={Building2} label="Active" value={d.active_tenants} color="text-brand-leaf"/>
        <Kpi icon={Users} label="Customers" value={d.total_customers}/>
        <Kpi icon={Truck} label="Riders" value={d.total_delivery_boys}/>
        <Kpi icon={Calendar} label="Active Subs" value={d.active_subscriptions}/>
        <Kpi icon={Package} label="Deliveries" value={d.total_deliveries}/>
        <Kpi icon={TrendingUp} label="Platform Revenue" value={formatINR(d.platform_revenue)} color="text-brand-sky"/>
        <Kpi icon={Sparkles} label="MRR (est.)" value={formatINR(d.active_tenants * 5000)} color="text-brand-clay"/>
      </div>

      <AtRiskRenewals risk={risk}/>
    </div>
  );
}

function AtRiskRenewals({ risk }) {
  if (!risk) return null;
  const empty = !risk.tenants || risk.tenants.length === 0;
  return (
    <section data-testid="at-risk-renewals">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h2 className="font-heading text-xl font-extrabold tracking-tight flex items-center gap-2">
          <AlertTriangle className={`w-5 h-5 ${empty ? "text-neutral-400" : "text-brand-clay"}`}/>
          At-risk renewals
          {!empty && <span className="px-2 py-0.5 rounded-full bg-brand-clay/15 text-brand-clay text-xs font-extrabold">{risk.at_risk_count}</span>}
        </h2>
        <div className="text-xs text-neutral-500">
          Next {risk.window_days} days · {empty ? "all tenants funded" : `total shortfall ${formatINR(risk.total_shortfall)}`}
        </div>
      </div>
      <div className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
        {empty ? (
          <EmptyState icon={Check} title="All tenants funded"
                      description="Every tenant has enough wallet balance to cover their next renewal."
                      testId="at-risk-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-5 py-3">Tenant</th>
                  <th className="text-right px-5 py-3">Wallet</th>
                  <th className="text-right px-5 py-3">Due (30d)</th>
                  <th className="text-right px-5 py-3">Shortfall</th>
                  <th className="text-left px-5 py-3">Next renewal</th>
                  <th className="text-left px-5 py-3">Items</th>
                  <th className="text-right px-5 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {risk.tenants.map((r) => (
                  <tr key={r.tenant_id} className="hover:bg-neutral-50" data-testid={`at-risk-row-${r.tenant_id}`}>
                    <td className="px-5 py-3">
                      <div className="font-semibold">{r.tenant_name}</div>
                      <div className="text-xs text-neutral-500 font-mono">{r.tenant_slug}</div>
                    </td>
                    <td className={`px-5 py-3 text-right font-mono font-bold ${r.wallet_balance < 0 ? "text-brand-clay" : ""}`}>
                      {formatINR(r.wallet_balance)}
                    </td>
                    <td className="px-5 py-3 text-right font-mono font-bold">{formatINR(r.due_within_window)}</td>
                    <td className="px-5 py-3 text-right font-mono font-extrabold text-brand-clay" data-testid={`at-risk-shortfall-${r.tenant_id}`}>
                      {formatINR(r.shortfall)}
                    </td>
                    <td className="px-5 py-3 text-sm font-mono">{(r.next_renewal_at || "—").slice(0, 10)}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {r.line_items.slice(0, 3).map((li, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-full bg-neutral-100 text-xs font-bold">
                            {li.kind === "plan" ? "📦" : "🧩"} {li.name} · {formatINR(li.amount)}
                          </span>
                        ))}
                        {r.line_items.length > 3 && (
                          <span className="text-xs text-neutral-500 font-bold">+{r.line_items.length - 3} more</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Link to="/super/wallets" className="text-brand-sky text-sm font-bold inline-flex items-center gap-0.5"
                            data-testid={`at-risk-topup-${r.tenant_id}`}>
                        <Wallet className="w-3.5 h-3.5"/> Top up <ChevronRight className="w-3 h-3"/>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function Kpi({ icon: Icon, label, value, color = "text-brand-ink" }) {
  return (
    <div className="bg-white rounded-3xl border border-neutral-200 p-4 sm:p-5" data-testid={`super-kpi-${label.toLowerCase().replace(/[ /]/g, '-')}`}>
      <div className="flex items-center justify-between gap-2">
        <Icon className={`w-5 h-5 shrink-0 ${color}`} />
        <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 text-right truncate">{label}</div>
      </div>
      <div className="font-heading text-2xl sm:text-3xl font-extrabold mt-3 tracking-tight truncate">{value}</div>
    </div>
  );
}

// UI-only kill switch for the "Clear Orders & Invoices" button — flip to
// true to bring it back. The backend endpoints, ClearOrdersDialog component,
// and every safeguard are untouched and still fully wired up; this only
// hides the entry point after production cleanup is done, so it can't be
// triggered by accident. Re-enable, run the cleanup, then flip back off.
const CLEAR_ORDERS_UI_ENABLED = false;

export function SuperTenants() {
  const [tenants, setTenants] = useState([]);
  const [addons, setAddons] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customTenant, setCustomTenant] = useState(null);
  // Per-app legal URLs panel — the Customer App and Rider App each need
  // their own Privacy/Terms URLs for Play Store / App Store listings.
  const [legalTenant, setLegalTenant] = useState(null);
  // Destructive "clear test orders/invoices" dialog — Super Admin only.
  const [clearOrdersTenant, setClearOrdersTenant] = useState(null);
  const [form, setForm] = useState({ name: "", slug: "", owner_email: "", plan: "starter", clone_from_tenant_id: "" });

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    const t = await api.get("/api/super-admin/tenants"); setTenants(t.data.tenants);
    const a = await api.get("/api/super-admin/addons"); setAddons(a.data.addons);
  };
  // Effect-only initial load: defined inline so the linter doesn't tie the
  // shared `load` (also called by click handlers) to a useEffect call site.
  useEffect(() => {
    const initialLoad = async () => {
      setLoadError(null);
      try {
        const t = await api.get("/api/super-admin/tenants"); setTenants(t.data.tenants);
        const a = await api.get("/api/super-admin/addons"); setAddons(a.data.addons);
      } catch (e) {
        setLoadError(friendlyMessage(e));
      } finally {
        setLoading(false);
      }
    };
    initialLoad();
  }, []);

  const toggleStatus = async (t) => {
    const next = t.status === "active" ? "suspended" : "active";
    await api.patch(`/api/super-admin/tenants/${t.id}/status`, { status: next });
    toast.success(`Tenant ${next}`); load();
  };

  const toggleModule = async (tid, key, enabled) => {
    await api.patch(`/api/super-admin/tenants/${tid}/modules`, { module_key: key, enabled });
    toast.success(`${key} ${enabled ? "enabled" : "disabled"}`); load();
    const updated = await api.get("/api/super-admin/tenants");
    const t = updated.data.tenants.find((x) => x.id === tid);
    if (t) setActive(t);
  };

  const toggleBottleTracking = async (tid, enabled) => {
    await api.patch(`/api/super-admin/tenants/${tid}/bottle-tracking`, { enabled });
    toast.success(`Bottle tracking ${enabled ? "enabled" : "disabled"}`); load();
    const updated = await api.get("/api/super-admin/tenants");
    const t = updated.data.tenants.find((x) => x.id === tid);
    if (t) setActive(t);
  };

  const toggleOrderEdit = async (tid, enabled) => {
    await api.patch(`/api/super-admin/tenants/${tid}/order-edit`, { enabled });
    toast.success(`Order editing ${enabled ? "enabled" : "disabled"}`); load();
    const updated = await api.get("/api/super-admin/tenants");
    const t = updated.data.tenants.find((x) => x.id === tid);
    if (t) setActive(t);
  };

  const toggleSubscriptionProductChange = async (tid, enabled) => {
    await api.patch(`/api/super-admin/tenants/${tid}/subscription-product-change`, { enabled });
    toast.success(`Subscription product change ${enabled ? "enabled" : "disabled"}`); load();
    const updated = await api.get("/api/super-admin/tenants");
    const t = updated.data.tenants.find((x) => x.id === tid);
    if (t) setActive(t);
  };

  const toggleBillSharing = async (tid, enabled) => {
    await api.patch(`/api/super-admin/tenants/${tid}/bill-sharing`, { enabled });
    toast.success(`Automatic bill sharing ${enabled ? "enabled" : "disabled"}`); load();
    const updated = await api.get("/api/super-admin/tenants");
    const t = updated.data.tenants.find((x) => x.id === tid);
    if (t) setActive(t);
  };

  // Refer & Earn is a core module — on for every tenant — with this as the
  // only off switch (backend: effective_modules.referral_switched_off).
  const toggleReferral = async (tid, enabled) => {
    try {
      await api.patch(`/api/super-admin/tenants/${tid}/referral`, { enabled });
      toast.success(`Refer & Earn ${enabled ? "enabled" : "disabled"} for this store`); load();
      const updated = await api.get("/api/super-admin/tenants");
      const t = updated.data.tenants.find((x) => x.id === tid);
      if (t) setActive(t);
    } catch (e) { toast.error(friendlyMessage(e)); }
  };

  const create = async () => {
    if (!form.name || !form.slug || !form.owner_phone) {
      toast.error("Name, slug and owner phone are required");
      return;
    }
    if (!/^\+91\d{10}$/.test(form.owner_phone)) {
      toast.error("Owner phone must be a 10-digit Indian mobile (we auto-prefix +91)");
      return;
    }
    const { data } = await api.post("/api/super-admin/tenants", form);
    const fb = data?.firebase_provisioning;
    const cc = data?.catalog_clone;
    const parts = [];
    if (cc?.attempted && !cc.error) {
      parts.push(`catalog cloned: ${cc.categories}c · ${cc.brands}b · ${cc.products}p · ${cc.banners}bn`);
    } else if (cc?.attempted && cc.error) {
      parts.push(`catalog clone failed: ${cc.error}`);
    }
    if (fb?.attempted && fb.successful_keys?.length) {
      parts.push(`Firebase: ${fb.successful_keys.length}/4 apps`);
    } else if (fb?.reason === "no_capacity") {
      parts.push("no Firebase capacity");
    } else if (fb?.error) {
      parts.push("Firebase failed (use Provision button)");
    }
    toast.success(parts.length ? `Tenant created · ${parts.join(" · ")}` : "Tenant created");
    setCreateOpen(false); load();
    setForm({ name: "", slug: "", owner_phone: "", owner_email: "", plan: "starter", clone_from_tenant_id: "" });
  };

  const provisionFirebase = async (t, force = false) => {
    const id = toast.loading(`Provisioning Firebase apps for ${t.name}…`);
    try {
      const url = `/api/super-admin/firebase/provision/${t.slug}${force ? "?force=true" : ""}`;
      const { data } = await api.post(url);
      toast.success(`${data.successful_keys?.length || 0}/4 apps provisioned on ${data.project_id}`, { id });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Firebase provisioning failed", { id });
    }
  };

  const copySlug = async (slug) => {
    try {
      await navigator.clipboard.writeText(slug);
      toast.success("Tenant slug copied");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  const retryLoad = () => {
    setLoading(true);
    setLoadError(null);
    load().catch((e) => setLoadError(friendlyMessage(e))).finally(() => setLoading(false));
  };

  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load tenants" description={loadError}
        action={<button onClick={retryLoad} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="super-tenants-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Customer Dairies</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Tenants ({tenants.length})</h1>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full sm:w-auto" data-testid="create-tenant-btn">
          <Plus className="w-4 h-4 mr-1" /> New Tenant
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {tenants.map((t) => {
          // Working URL: path-based route /t/<slug> on the current platform origin.
          // The React app's <Route path="/t/:slug"> handler stores slug in
          // localStorage and forwards to /app/home, so this always works
          // regardless of wildcard DNS setup.
          const origin = (typeof window !== "undefined" && window.location.origin) || "";
          const liveUrl = `${origin}/t/${t.slug}`;
          // "Aspirational" subdomain (requires wildcard *.localappstore.in DNS)
          const subdomainUrl = `https://${t.slug}.localappstore.in`;
          return (
          <div key={t.id} className="bg-white rounded-3xl border border-neutral-200 p-6" data-testid={`tenant-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              {/* min-w-0 is load-bearing: without it this flex item won't
                  shrink below its content's natural width (the `truncate`
                  classes further down do nothing without it either), and
                  the status pill on the right gets pushed off the edge of
                  narrow viewports — causing page-level horizontal scroll. */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ background: t.branding?.primary_color || "#3A86FF" }} />
                  <div className="font-heading text-xl font-extrabold truncate">{t.name}</div>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-xs font-mono text-neutral-500" data-testid={`tenant-slug-${t.id}`}>{t.slug}</span>
                  <button onClick={() => copySlug(t.slug)}
                          className="h-6 w-6 rounded-lg hover:bg-neutral-100 inline-flex items-center justify-center shrink-0"
                          title="Copy tenant slug" data-testid={`copy-slug-${t.id}`}>
                    <Copy className="w-3 h-3 text-neutral-400" />
                  </button>
                </div>
                <div className="mt-1.5 space-y-0.5">
                  <a href={liveUrl} target="_blank" rel="noreferrer"
                     className="block text-sm font-mono text-brand-sky hover:underline truncate"
                     data-testid={`tenant-live-url-${t.id}`}
                     title="Working URL — opens this tenant's storefront">
                    {liveUrl}
                  </a>
                  <div className="text-[11px] text-neutral-400 font-mono truncate"
                       data-testid={`tenant-subdomain-${t.id}`}
                       title="Future subdomain — requires wildcard *.localappstore.in DNS">
                    {subdomainUrl} <span className="text-neutral-300">· DNS pending</span>
                  </div>
                </div>
                <div className="text-xs text-neutral-500 mt-1 space-y-0.5">
                  {t.owner_phone && (
                    <div className="font-mono" data-testid={`tenant-phone-${t.id}`}>📱 {t.owner_phone}</div>
                  )}
                  {t.owner_email && (
                    <div className="truncate" data-testid={`tenant-email-${t.id}`}>{t.owner_email}</div>
                  )}
                </div>
              </div>
              <span className={`shrink-0 px-3 py-1 rounded-full text-xs font-bold uppercase ${
                t.status === "active" ? "bg-brand-leaf/10 text-brand-leaf" : "bg-brand-clay/10 text-brand-clay"
              }`}>{t.status}</span>
            </div>
            <div className="grid grid-cols-4 gap-3 mt-5 pt-5 border-t border-neutral-100">
              <Stat label="Plan" value={
                t.custom_plan
                  ? <span className="inline-flex items-center gap-1 text-brand-clay" title={t.custom_plan.name}><Crown className="w-3.5 h-3.5"/> Custom</span>
                  : <span className="capitalize">{t.plan}</span>
              }/>
              <Stat label="Customers" value={t.customers}/>
              <Stat label="Subs" value={t.subs}/>
              <Stat label="Revenue" value={formatINR(t.revenue)}/>
            </div>
            {t.custom_plan && (
              <div className="mt-4 p-3 rounded-2xl bg-brand-clay/5 border border-brand-clay/20 text-xs" data-testid={`custom-plan-summary-${t.id}`}>
                <div className="flex items-center gap-1.5 font-bold text-brand-clay">
                  <Crown className="w-3.5 h-3.5"/> {t.custom_plan.name}
                </div>
                <div className="text-neutral-600 mt-1">
                  {formatINR(t.custom_plan.monthly_price)}/mo · {formatINR(t.custom_plan.yearly_price)}/yr
                  {t.custom_plan.included_addons?.length ? ` · ${t.custom_plan.included_addons.length} addon(s) included` : ""}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-5">
              <button onClick={() => { setActive(t); setOpen(true); }} className="flex-1 min-w-[150px] h-10 rounded-2xl bg-brand-cream border border-neutral-200 font-bold text-sm inline-flex items-center justify-center gap-1.5" data-testid={`manage-${t.id}`}>
                <Settings className="w-4 h-4" /> Modules
              </button>
              <button onClick={() => { setCustomTenant(t); setCustomOpen(true); }}
                      className="flex-1 min-w-[150px] h-10 rounded-2xl bg-brand-clay/10 text-brand-clay font-bold text-sm inline-flex items-center justify-center gap-1.5"
                      data-testid={`custom-plan-${t.id}`}>
                <Crown className="w-4 h-4" /> {t.custom_plan ? "Edit Custom Plan" : "Custom Plan"}
              </button>
              <FirebaseTenantButton tenant={t} onProvision={provisionFirebase}/>
              <button onClick={() => setLegalTenant(t)}
                      className="h-10 px-4 rounded-2xl bg-brand-sky/10 text-brand-sky font-bold text-sm inline-flex items-center gap-1.5"
                      data-testid={`legal-urls-${t.id}`}>
                <FileText className="w-4 h-4" /> Legal URLs
              </button>
              <button onClick={() => toggleStatus(t)} className={`h-10 px-4 rounded-2xl font-bold text-sm inline-flex items-center gap-1.5 ${
                t.status === "active" ? "bg-brand-clay/10 text-brand-clay" : "bg-brand-leaf/10 text-brand-leaf"
              }`} data-testid={`toggle-status-${t.id}`}>
                <Power className="w-4 h-4" /> {t.status === "active" ? "Suspend" : "Activate"}
              </button>
              {CLEAR_ORDERS_UI_ENABLED && (
                <button onClick={() => setClearOrdersTenant(t)}
                        className="h-10 px-4 rounded-2xl bg-brand-clay/10 text-brand-clay font-bold text-sm inline-flex items-center gap-1.5"
                        data-testid={`clear-orders-${t.id}`}>
                  <Trash2 className="w-4 h-4" /> Clear Orders &amp; Invoices
                </button>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {/* Legal URLs Dialog — per-app Privacy & Terms for Play Store / App Store listings */}
      <LegalUrlsDialog tenant={legalTenant} onClose={() => setLegalTenant(null)} />

      {/* Clear Orders & Invoices — destructive, Super Admin only */}
      <ClearOrdersDialog tenant={clearOrdersTenant} onClose={() => setClearOrdersTenant(null)} />

      {/* Modules Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{active?.name} — Modules</DialogTitle></DialogHeader>

          {/* Tenant Logo upload — 1024×1024 master used by build CLI to derive
              512×512 app icon + 1024×500 Play Feature Graphic */}
          {active && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
              <TenantLogoUploader tenant={active} persona="customer" onUploaded={load} />
              <TenantLogoUploader tenant={active} persona="rider" onUploaded={load} />
            </div>
          )}

          {/* Tenant splash image — native launch screen (next app build) +
              in-app hand-off splash (no rebuild). Keyed by tenant so local
              state never leaks between tenants. */}
          {active && <TenantSplashUploader key={active.id} tenant={active} onChanged={load} />}

          {/* Standard Bottle Tracking — distinct from `qr_bottle` (paid add-on).
              This is a per-tenant flag that hides the bottle UI everywhere:
              customer app pill, rider return prompt, admin Bottles tab. */}
          {active && (
            <div className="p-4 rounded-2xl bg-brand-sky/10 border border-brand-sky/30 flex items-center gap-3 mb-3" data-testid="super-bottle-tracking-row">
              <div className="flex-1">
                <div className="font-bold flex items-center gap-2">
                  Bottle Tracking (standard)
                  {active.bottle_tracking_enabled !== false && <Check className="w-4 h-4 text-brand-leaf"/>}
                </div>
                <div className="text-xs text-neutral-600 mt-0.5">
                  Enables the built-in bottle-return workflow for milk/buttermilk.
                  Turn off if this tenant sells only non-bottled goods. This is
                  separate from the paid <b>Smart Bottle QR Tracking</b> add-on below.
                </div>
              </div>
              <Switch
                checked={active.bottle_tracking_enabled !== false}
                onCheckedChange={(v) => toggleBottleTracking(active.id, v)}
                data-testid="super-toggle-bottle-tracking"
              />
            </div>
          )}

          {/* Order editing — super-admin-only restriction. Tenant admins
              cannot see or flip this themselves; it defaults on for every
              tenant (matches the current live behavior of the admin order
              edit feature) and exists purely so the platform owner can
              restrict a specific tenant if ever needed. */}
          {active && (
            <div className="p-4 rounded-2xl bg-brand-sky/10 border border-brand-sky/30 flex items-center gap-3 mb-3" data-testid="super-order-edit-row">
              <div className="flex-1">
                <div className="font-bold flex items-center gap-2">
                  Order Editing
                  {active.order_edit_enabled !== false && <Check className="w-4 h-4 text-brand-leaf"/>}
                </div>
                <div className="text-xs text-neutral-600 mt-0.5">
                  Lets this tenant's admins edit a placed/confirmed order's product, quantity,
                  date, slot, address, or instructions. On by default — turn off to restrict
                  this store to rider reassignment + confirm/cancel only.
                </div>
              </div>
              <Switch
                checked={active.order_edit_enabled !== false}
                onCheckedChange={(v) => toggleOrderEdit(active.id, v)}
                data-testid="super-toggle-order-edit"
              />
            </div>
          )}

          {/* Subscription product change — super-admin-only, per tenant, and
              OFF for every store until switched on here (a store that never
              had the setting is off). Tenant admins can't see or flip it.
              On: customers and this store's admins can move a subscription
              to another product / pack size from its Edit screen, and a
              per-day quantity change is priced at that day's own pack size. */}
          {active && (
            <div className="p-4 rounded-2xl bg-brand-sky/10 border border-brand-sky/30 flex items-center gap-3 mb-3" data-testid="super-subscription-product-change-row">
              <div className="flex-1">
                <div className="font-bold flex items-center gap-2">
                  Subscription Product Change
                  {active.subscription_product_change_enabled === true && <Check className="w-4 h-4 text-brand-leaf"/>}
                </div>
                <div className="text-xs text-neutral-600 mt-0.5">
                  Lets this store's customers and admins move an existing subscription to another
                  product or pack size (e.g. 1000 ml → 500 ml) without cancelling it. Off by default —
                  turn on only for a store that has asked for it.
                </div>
              </div>
              <Switch
                checked={active.subscription_product_change_enabled === true}
                onCheckedChange={(v) => toggleSubscriptionProductChange(active.id, v)}
                data-testid="super-toggle-subscription-product-change"
              />
            </div>
          )}

          {/* Automatic Bill Sharing — super-admin-only, per-tenant master
              switch for the whole feature (periodic weekly/monthly bill +
              per-order confirmation, push and SMS/WhatsApp alike). Tenant
              admins can't see or flip this — it only controls whether their
              own channel picker (Settings → Order & bill notifications) does
              anything. On by default so every tenant's existing periodic
              bill push keeps working unchanged. */}
          {active && (
            <div className="p-4 rounded-2xl bg-brand-sky/10 border border-brand-sky/30 flex items-center gap-3 mb-3" data-testid="super-bill-sharing-row">
              <div className="flex-1">
                <div className="font-bold flex items-center gap-2">
                  Automatic Bill Sharing
                  {active.bill_sharing_enabled !== false && <Check className="w-4 h-4 text-brand-leaf"/>}
                </div>
                <div className="text-xs text-neutral-600 mt-0.5">
                  Master switch for this tenant's order-placed confirmations and periodic bill
                  notifications (push + SMS/WhatsApp). Off disables it entirely for this store —
                  admins lose the channel picker, customers get nothing.
                </div>
              </div>
              <Switch
                checked={active.bill_sharing_enabled !== false}
                onCheckedChange={(v) => toggleBillSharing(active.id, v)}
                data-testid="super-toggle-bill-sharing"
              />
            </div>
          )}

          {active && (
            <div className="p-4 rounded-2xl bg-brand-cream flex items-center gap-3" data-testid="super-referral-row">
              <div className="flex-1">
                <div className="font-bold flex items-center gap-2">
                  Refer &amp; Earn
                  {active.referral_enabled !== false && <Check className="w-4 h-4 text-brand-leaf"/>}
                </div>
                <div className="text-xs text-neutral-600 mt-0.5">
                  Included for every store. Off removes it entirely for this tenant — customer page,
                  signup code field, admin Referrals menu and reward payouts. Referrals already recorded
                  are kept and resume when switched back on. Reward amounts stay with the tenant admin.
                </div>
              </div>
              <Switch
                checked={active.referral_enabled !== false}
                onCheckedChange={(v) => toggleReferral(active.id, v)}
                data-testid="super-toggle-referral"
              />
            </div>
          )}

          <div className="space-y-2 mt-2">
            {addons.map((a) => {
              const enabled = active?.enabled_modules?.includes(a.key);
              return (
                <div key={a.key} className="p-4 rounded-2xl bg-brand-cream flex items-center gap-3" data-testid={`addon-${a.key}`}>
                  <div className="flex-1">
                    <div className="font-bold flex items-center gap-2">{a.name} {enabled && <Check className="w-4 h-4 text-brand-leaf"/>}</div>
                    <div className="text-xs text-neutral-600">{a.description}</div>
                    <div className="text-xs text-brand-sky font-bold mt-1">{formatINR(a.monthly_price)}/mo</div>
                  </div>
                  <Switch checked={enabled} onCheckedChange={(v) => toggleModule(active.id, a.key, v)} data-testid={`toggle-${a.key}`}/>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New Tenant</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Dairy name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 rounded-2xl" data-testid="tenant-name"/>
            <Input placeholder="Slug (e.g. dairyfresh) *" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="h-12 rounded-2xl font-mono" data-testid="tenant-slug"/>
            <div className="flex items-stretch gap-2">
              <div className="h-12 px-3 rounded-2xl bg-brand-cream border border-neutral-200 inline-flex items-center font-mono text-sm text-neutral-700 shrink-0 select-none" data-testid="tenant-phone-prefix">
                +91
              </div>
              <Input
                placeholder="10-digit mobile *"
                value={(form.owner_phone || "").replace(/^\+91/, "")}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setForm({ ...form, owner_phone: digits ? `+91${digits}` : "" });
                }}
                inputMode="numeric"
                maxLength={10}
                className="h-12 rounded-2xl font-mono flex-1"
                data-testid="tenant-phone"
              />
            </div>
            <Input placeholder="Owner email (optional)" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="h-12 rounded-2xl" data-testid="tenant-email"/>
            <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="h-12 rounded-2xl border border-neutral-200 px-4 w-full bg-white" data-testid="tenant-plan">
              <option value="starter">Starter — ₹2,999/mo</option>
              <option value="growth">Growth — ₹7,999/mo</option>
              <option value="enterprise">Enterprise — ₹19,999/mo</option>
            </select>
            <div className="border-t border-neutral-200 pt-3">
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-2 mb-1.5">
                <Copy className="w-3.5 h-3.5"/> Clone catalog from (optional)
              </label>
              <select
                value={form.clone_from_tenant_id}
                onChange={(e) => setForm({ ...form, clone_from_tenant_id: e.target.value })}
                className="h-12 rounded-2xl border border-neutral-200 px-4 w-full bg-white"
                data-testid="tenant-clone-from"
              >
                <option value="">— none (blank tenant) —</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} · {t.slug}</option>
                ))}
              </select>
              <p className="text-[11px] text-neutral-500 mt-1">
                Copies categories, brands, products &amp; banners. Users, wallets and orders are NOT copied.
              </p>
            </div>
            <p className="text-[11px] text-neutral-500">
              The owner logs in to the Tenant Admin app using this phone + OTP. Email is optional and used for contact only.
            </p>
            <Button onClick={create} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-tenant-btn">Create</Button>
          </div>
        </DialogContent>
      </Dialog>

      <CustomPlanDialog
        open={customOpen} onOpenChange={setCustomOpen}
        tenant={customTenant} addons={addons}
        onSaved={() => { load(); setCustomOpen(false); }}
      />
    </div>
  );
}

function TenantLogoUploader({ tenant, persona = "customer", onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // last upload response from API
  const [validationError, setValidationError] = useState("");
  const inputRef = React.useRef(null);
  const branding = tenant?.branding || {};
  // Persona-specific logo with fallback to the legacy single `logo_url`
  // (for tenants that uploaded before personas existed — customer only).
  // Prefer the URL from the most recent upload response over the
  // parent-passed tenant snapshot so the preview swaps INSTANTLY after
  // a successful re-upload — even before the parent re-fetches the
  // tenant doc. Combined with the backend cache-bust ?v=… suffix this
  // guarantees the new image is what the browser renders.
  const personaKey = `${persona}_logo_url`;
  const liveLogo = result?.url
    || branding[personaKey]
    || (persona === "customer" ? branding.logo_url : "")
    || "";
  const personaLabel = persona.charAt(0).toUpperCase() + persona.slice(1);

  // Client-side dimension validation BEFORE upload (so user gets immediate feedback)
  const validate = (file) => new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject("Only image files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      reject("Logo must be ≤ 5 MB");
      return;
    }
    const img = new Image();
    img.onload = () => {
      if (img.width !== 1024 || img.height !== 1024) {
        reject(`Logo must be exactly 1024×1024 px (got ${img.width}×${img.height})`);
      } else {
        resolve(file);
      }
    };
    img.onerror = () => reject("Could not decode image");
    img.src = URL.createObjectURL(file);
  });

  const upload = async (file) => {
    setValidationError("");
    try { await validate(file); }
    catch (e) { setValidationError(String(e)); return; }

    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/api/super-admin/tenants/${tenant.id}/branding/logo?persona=${persona}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data);
      toast.success(`${personaLabel} logo uploaded to ${data.storage_backend === "s3" ? "AWS S3" : "local storage"}`);
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";  // allow re-selecting the same file
  };

  const backend = result?.storage_backend
    || branding[`${persona}_logo_storage_backend`]
    || (persona === "customer" ? branding.logo_storage_backend : undefined);
  const onS3 = backend === "s3";

  return (
    <div className="border border-neutral-200 rounded-2xl p-4 bg-brand-cream/30" data-testid={`tenant-logo-uploader-${persona}`}>
      <div className="flex items-start gap-3">
        <div className="w-16 h-16 rounded-xl border border-neutral-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
          {liveLogo ? (
            <img src={liveLogo} alt={`${personaLabel} logo`} className="w-full h-full object-contain" data-testid={`tenant-logo-preview-${persona}`}/>
          ) : (
            <ImageIcon className="w-7 h-7 text-neutral-300"/>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm flex items-center gap-2">
            {personaLabel} App Logo (1024×1024)
            {liveLogo && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${onS3 ? "bg-brand-leaf/15 text-brand-leaf" : "bg-amber-100 text-amber-800"}`}
                    data-testid={`tenant-logo-storage-badge-${persona}`}>
                {onS3 ? <><CheckCircle2 className="w-3 h-3 inline mr-0.5"/>S3</> : "Local"}
              </span>
            )}
          </div>
          <div className="text-xs text-neutral-500 mt-0.5">
            Master image used by the build CLI to derive the {personaLabel} app&apos;s 512×512 icon and 1024×500 Feature Graphic.
          </div>
          {liveLogo && (
            <a href={liveLogo} target="_blank" rel="noreferrer"
               className="text-[11px] font-mono text-brand-sky hover:underline break-all block mt-1"
               data-testid={`tenant-logo-url-${persona}`} title="Click to verify the upload is publicly accessible">
              {liveLogo}
            </a>
          )}
          {validationError && (
            <div className="text-xs text-brand-clay mt-1.5 font-medium" data-testid={`tenant-logo-validation-error-${persona}`}>{validationError}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <input ref={inputRef} type="file" accept="image/png,image/jpeg" onChange={onPick} className="hidden" data-testid={`tenant-logo-input-${persona}`}/>
        <Button onClick={() => inputRef.current?.click()} disabled={uploading}
                className="h-9 rounded-2xl bg-brand-sky hover:bg-blue-700 text-white font-bold text-sm inline-flex items-center gap-1.5 px-4"
                data-testid={`tenant-logo-upload-btn-${persona}`}>
          <UploadCloud className="w-4 h-4"/>
          {uploading ? "Uploading…" : liveLogo ? `Replace ${personaLabel} logo` : `Upload ${personaLabel} logo`}
        </Button>
        {result && (
          <span className="text-[11px] text-neutral-500" data-testid={`tenant-logo-meta-${persona}`}>
            {result.size_bytes ? `${(result.size_bytes / 1024).toFixed(1)} KB` : ""}
            {result.bucket ? ` · ${result.bucket}` : ""}
          </span>
        )}
      </div>
    </div>
  );
}


// Mirrors the server-side rules in routes_super_admin.py (_inspect_splash_image).
const SPLASH_TYPES = ["image/png", "image/jpeg", "image/webp"];
const SPLASH_MAX_BYTES = 2 * 1024 * 1024;
const SPLASH_MIN_SIDE = 1242;
const SPLASH_MAX_SIDE = 8192;
const SPLASH_HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const SPLASH_PREVIEW_FRAMES = [
  { key: "phone", label: "Phone · 9:19.5", width: 84, ratio: 19.5 / 9, icon: Smartphone },
  { key: "tablet", label: "Tablet · 3:4", width: 126, ratio: 4 / 3, icon: Tablet },
];

function TenantSplashUploader({ tenant, onChanged }) {
  const branding = tenant?.branding || {};
  const primary = branding.primary_color || "#3A86FF";
  // Local copies so the preview updates instantly after an upload / remove /
  // colour save, before (and regardless of) the parent's tenant re-fetch.
  const [splashUrl, setSplashUrl] = useState(branding.splash_url || "");
  const [dims, setDims] = useState(branding.splash_dimensions || null);
  const [savedBg, setSavedBg] = useState(branding.splash_bg_color || "");
  const [bgInput, setBgInput] = useState(branding.splash_bg_color || "");
  const [busy, setBusy] = useState("");            // "" | "upload" | "remove" | "bg"
  const [validationError, setValidationError] = useState("");
  const inputRef = React.useRef(null);

  const bgInputValid = bgInput === "" || SPLASH_HEX_RE.test(bgInput);
  // What devices will use: the saved colour, else the brand colour. The
  // preview follows the input as soon as it holds a valid value ("" = the
  // brand colour), so the admin sees the result before saving.
  const effectiveBg = bgInput === "" ? primary
    : SPLASH_HEX_RE.test(bgInput) ? bgInput
      : (SPLASH_HEX_RE.test(savedBg) ? savedBg : primary);
  const bgDirty = bgInput !== savedBg;
  // <input type="color"> only takes #rrggbb.
  const pickerValue = SPLASH_HEX_RE.test(effectiveBg) ? effectiveBg.toLowerCase() : "#3a86ff";

  const validate = (file) => new Promise((resolve, reject) => {
    if (!SPLASH_TYPES.includes(file.type)) {
      reject("Only PNG, JPEG or WebP images are allowed");
      return;
    }
    if (file.size > SPLASH_MAX_BYTES) {
      reject(`Splash image must be ≤ 2 MB (this one is ${(file.size / (1024 * 1024)).toFixed(1)} MB)`);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const w = img.naturalWidth, h = img.naturalHeight;
      if (w < SPLASH_MIN_SIDE || h < SPLASH_MIN_SIDE) {
        reject(`Splash image must be at least ${SPLASH_MIN_SIDE}×${SPLASH_MIN_SIDE} px (got ${w}×${h})`);
      } else if (w > SPLASH_MAX_SIDE || h > SPLASH_MAX_SIDE) {
        reject(`Splash image can be at most ${SPLASH_MAX_SIDE} px on either side (got ${w}×${h})`);
      } else {
        resolve(file);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject("Could not decode image"); };
    img.src = url;
  });

  const upload = async (file) => {
    setValidationError("");
    try { await validate(file); }
    catch (e) { setValidationError(String(e)); return; }
    setBusy("upload");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/api/super-admin/tenants/${tenant.id}/branding/splash`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setSplashUrl(data.url);
      setDims(data.dimensions || null);
      toast.success("Splash image uploaded");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy("");
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";  // allow re-selecting the same file
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${tenant.name}'s splash image? The apps go back to the default logo splash.`)) return;
    setBusy("remove");
    try {
      await api.delete(`/api/super-admin/tenants/${tenant.id}/branding/splash`);
      setSplashUrl("");
      setDims(null);
      toast.success("Splash image removed");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove the splash image");
    } finally {
      setBusy("");
    }
  };

  const saveBg = async (value) => {
    if (value !== "" && !SPLASH_HEX_RE.test(value)) {
      toast.error("Use a hex colour like #1A2B3C, or leave it empty for the brand colour");
      return;
    }
    setBusy("bg");
    try {
      await api.patch(`/api/super-admin/tenants/${tenant.id}/branding`, { splash_bg_color: value });
      setSavedBg(value);
      setBgInput(value);
      toast.success(value ? `Splash background set to ${value}` : "Splash background reset to the brand colour");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the background colour");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="border border-neutral-200 rounded-2xl p-4 bg-brand-cream/30 mb-4" data-testid="tenant-splash-uploader">
      <div className="font-bold text-sm flex items-center gap-2">
        <ImageIcon className="w-4 h-4 text-neutral-500"/> Splash screen
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${splashUrl ? "bg-brand-leaf/15 text-brand-leaf" : "bg-neutral-100 text-neutral-500"}`}
              data-testid="tenant-splash-status">
          {splashUrl ? "Custom" : "Default (logo)"}
        </span>
      </div>
      <div className="text-xs text-neutral-500 mt-0.5">
        Full-screen image shown while the {tenant.name} apps start. Shared by the Customer and Rider apps.
      </div>

      {/* The same rule every device uses (native launch screens in
          scripts/build_app.py, the in-app overlay in public/index.html):
          the artwork COVERS the screen with its aspect ratio intact and the
          overflow cropped from the centre — no bars. With no splash image
          the screen is simply the background colour. */}
      <div className="flex flex-wrap items-end gap-4 mt-3" data-testid="tenant-splash-previews">
        {SPLASH_PREVIEW_FRAMES.map((f) => {
          const height = Math.round(f.width * f.ratio);
          const Icon = f.icon;
          return (
            <div key={f.key} className="flex flex-col items-center gap-1">
              <div className="rounded-[14px] border-[3px] border-neutral-800 overflow-hidden flex items-center justify-center shadow-sm"
                   style={{ width: f.width, height, background: effectiveBg }}
                   data-testid={`tenant-splash-preview-${f.key}`}>
                {splashUrl ? (
                  <img src={splashUrl} alt={`${tenant.name} splash (${f.label})`}
                       style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
                       data-testid={`tenant-splash-preview-img-${f.key}`}/>
                ) : null}
              </div>
              <div className="text-[10px] text-neutral-500 font-medium inline-flex items-center gap-1">
                <Icon className="w-3 h-3"/> {f.label}
              </div>
            </div>
          );
        })}
        <div className="flex-1 min-w-0 text-[11px] text-neutral-500 self-center">
          {splashUrl ? (
            <>
              {dims?.width ? <div className="font-mono" data-testid="tenant-splash-dimensions">{dims.width}×{dims.height} px</div> : null}
              <a href={splashUrl} target="_blank" rel="noreferrer"
                 className="font-mono text-brand-sky hover:underline break-all block mt-0.5"
                 data-testid="tenant-splash-url" title="Click to verify the upload is publicly accessible">
                {splashUrl}
              </a>
            </>
          ) : (
            <span>No splash image — the apps show the background colour, full screen.</span>
          )}
        </div>
      </div>

      {validationError && (
        <div className="text-xs text-brand-clay mt-2 font-medium" data-testid="tenant-splash-validation-error">{validationError}</div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onPick} className="hidden" data-testid="tenant-splash-input"/>
        <Button onClick={() => inputRef.current?.click()} disabled={!!busy}
                className="h-9 rounded-2xl bg-brand-sky hover:bg-blue-700 text-white font-bold text-sm inline-flex items-center gap-1.5 px-4"
                data-testid="tenant-splash-upload-btn">
          <UploadCloud className="w-4 h-4"/>
          {busy === "upload" ? "Uploading…" : splashUrl ? "Replace splash image" : "Upload splash image"}
        </Button>
        {splashUrl && (
          <button onClick={remove} disabled={!!busy}
                  className="h-9 px-4 rounded-2xl bg-brand-clay/10 text-brand-clay font-bold text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="tenant-splash-remove-btn">
            <Trash2 className="w-4 h-4"/> {busy === "remove" ? "Removing…" : "Remove"}
          </button>
        )}
      </div>

      <div className="mt-3">
        <div className="text-xs font-bold text-neutral-700 flex items-center gap-1.5">
          <Palette className="w-3.5 h-3.5"/> Background colour
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          <input type="color" value={pickerValue} onChange={(e) => setBgInput(e.target.value.toUpperCase())}
                 className="h-9 w-12 rounded-xl border border-neutral-200 cursor-pointer shrink-0"
                 disabled={!!busy} data-testid="tenant-splash-bg-picker"/>
          <Input value={bgInput} onChange={(e) => setBgInput(e.target.value.trim())}
                 placeholder={`${primary} (brand colour)`} maxLength={7}
                 className={`h-9 w-36 rounded-2xl font-mono text-xs ${bgInputValid ? "" : "border-brand-clay"}`}
                 disabled={!!busy} data-testid="tenant-splash-bg-input"/>
          <Button onClick={() => saveBg(bgInput)} disabled={!!busy || !bgDirty || !bgInputValid}
                  className="h-9 rounded-2xl bg-brand-ink text-white font-bold text-xs px-3"
                  data-testid="tenant-splash-bg-save-btn">
            {busy === "bg" ? "Saving…" : "Save colour"}
          </Button>
          {savedBg && (
            <button onClick={() => saveBg("")} disabled={!!busy}
                    className="h-9 px-3 rounded-2xl text-xs font-bold text-neutral-600 hover:bg-neutral-100 inline-flex items-center gap-1"
                    data-testid="tenant-splash-bg-reset-btn">
              <RotateCcw className="w-3.5 h-3.5"/> Use brand colour
            </button>
          )}
        </div>
        {!bgInputValid && (
          <div className="text-[11px] text-brand-clay mt-1" data-testid="tenant-splash-bg-error">Use a hex colour like #1A2B3C</div>
        )}
      </div>

      <ul className="mt-3 text-[11px] text-neutral-500 list-disc pl-4 space-y-0.5" data-testid="tenant-splash-guidance">
        <li>PNG, JPEG or WebP · max 2 MB · recommended 2732×2732 px, minimum 1242×1242 px.</li>
        <li>Keep the artwork inside the central safe area — edges can sit close to the screen on narrow phones.</li>
        <li>Transparent PNGs are supported — transparency shows the background colour.</li>
        <li>The image fills the whole screen with its aspect ratio intact — never stretched. Screen shapes differ, so the edges are cropped evenly from the centre: keep the important artwork inside the safe area (see previews).</li>
        <li>Android 12+ first shows the tenant logo on this background colour (an OS restriction — the apps use the same style on older Android), then the full splash image.</li>
        <li>The in-app splash updates without an app rebuild (from the next launch after the app has loaded it); the native launch screen picks up changes on the next app build.</li>
      </ul>
    </div>
  );
}


function FirebaseTenantButton({ tenant, onProvision }) {
  const [status, setStatus] = useState(null);
  const [byoOpen, setByoOpen] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/api/super-admin/firebase/tenant/${tenant.slug}/status`);
      setStatus(data);
    } catch { setStatus(null); }
  };
  useEffect(() => { refresh(); }, [tenant.slug, tenant.firebase_provisioned_at]);

  const count = status?.successful_count ?? 0;
  const expected = status?.expected_count ?? 4;
  const complete = status?.complete;
  const projectId = status?.project_id;
  // Whether the tenant→project→service-account chain actually terminates in
  // usable credentials. All four apps can be provisioned and push still send
  // nothing, because the project they point at has no service account.
  const pushReady = status?.push_ready;
  const credSource = status?.credential_source;
  const byoCount = Object.values(status?.slots || {})
    .filter(s => s?.source === "byo").length;

  const tone = complete
    ? "bg-brand-leaf/10 text-brand-leaf border-brand-leaf/30"
    : count === 0
      ? "bg-orange-50 text-orange-700 border-orange-200"
      : "bg-amber-50 text-amber-800 border-amber-200";
  const label = count === 0
    ? "Provision Firebase"
    : `Firebase ${count}/${expected}${byoCount ? ` · ${byoCount} BYO` : ""}`;

  return (
    <>
      <div className="flex-1 min-w-[150px] flex gap-1">
        <button
          onClick={() => count === 0 ? onProvision(tenant, false) : onProvision(tenant, true)}
          className={`flex-1 h-10 rounded-2xl font-bold text-sm inline-flex items-center justify-center gap-1.5 border ${tone}`}
          data-testid={`fb-status-${tenant.id}`}
          title={projectId ? `Project: ${projectId}` : "Auto-provision 4 Firebase apps"}>
          <Flame className="w-4 h-4" /> {label}
          {count > 0 && !complete && <RefreshCw className="w-3.5 h-3.5 opacity-70" />}
        </button>
        <button
          onClick={() => setByoOpen(true)}
          className="h-10 px-2.5 rounded-2xl border border-neutral-300 text-neutral-600 hover:bg-neutral-100"
          data-testid={`fb-manage-slots-${tenant.id}`}
          title="Bring-your-own — upload existing Firebase configs per slot">
          <Settings className="w-4 h-4"/>
        </button>
      </div>

      {/* The tenant→Firebase mapping, shown rather than hidden in a tooltip.
          With tenants spread across different Firebase projects (they are
          capacity-limited, and operators can bring their own), "which project
          is this tenant on?" has to be answerable at a glance. */}
      <div className="w-full basis-full text-[10px] leading-tight mt-1 flex items-center gap-1.5 flex-wrap"
           data-testid={`fb-project-${tenant.id}`}>
        {projectId ? (
          <>
            <span className="font-mono font-bold text-neutral-700">{projectId}</span>
            {status?.project_display_name && status.project_display_name !== projectId && (
              <span className="text-neutral-400">({status.project_display_name})</span>
            )}
            <span className={`px-1.5 py-0.5 rounded-full font-extrabold uppercase tracking-wider ${
              pushReady ? "bg-brand-leaf/10 text-brand-leaf" : "bg-brand-clay/10 text-brand-clay"
            }`} data-testid={`fb-push-ready-${tenant.id}`}>
              {pushReady ? `push ok · ${credSource}` : "no service account"}
            </span>
          </>
        ) : (
          <span className="text-neutral-400">No Firebase project assigned</span>
        )}
      </div>
      <FirebaseByoDialog
        open={byoOpen}
        onOpenChange={setByoOpen}
        tenant={tenant}
        status={status}
        onChanged={refresh}
      />
    </>
  );
}


const SLOT_LABELS = {
  customer_android: "Customer · Android",
  customer_ios: "Customer · iOS",
  rider_android: "Rider · Android",
  rider_ios: "Rider · iOS",
};

function FirebaseByoDialog({ open, onOpenChange, tenant, status, onChanged }) {
  const slots = status?.slots || {};
  const [busy, setBusy] = useState(null);
  const [audit, setAudit] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const { data } = await api.get(`/api/super-admin/firebase/tenant/${tenant.slug}/audit`);
      setAudit(data.events || []);
    } catch (e) {
      toast.error("Audit log unavailable");
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => { if (open && auditOpen) loadAudit(); }, [open, auditOpen, tenant.slug]);

  const clearSlot = async (key) => {
    if (!confirm(`Clear the BYO config for ${SLOT_LABELS[key]}? Next Provision will fill it via auto-provision.`)) return;
    setBusy(key);
    try {
      await api.delete(`/api/super-admin/firebase/tenant/${tenant.slug}/apps/${key}/manual`);
      toast.success(`Cleared ${SLOT_LABELS[key]}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`fb-byo-dialog-${tenant.id}`}>
        <DialogHeader>
          <DialogTitle>Firebase Slots — {tenant.name}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-neutral-500 mb-3">
          Upload an existing Firebase app&apos;s config (Bring-your-own) for any slot, or leave empty
          to let the auto-provisioner create it via the &quot;Provision Firebase&quot; button.
          BYO slots are LOCKED — auto-provision never overwrites them. App ID &amp; identifier
          are auto-parsed from the uploaded file when left blank.
        </p>
        <div className="space-y-2">
          {["customer_android", "customer_ios", "rider_android", "rider_ios"].map((k) => {
            const s = slots[k] || { state: "empty", source: null };
            return (
              <FirebaseSlotRow
                key={k}
                slotKey={k}
                slot={s}
                busy={busy === k}
                tenantSlug={tenant.slug}
                onUploaded={onChanged}
                onClear={() => clearSlot(k)}
              />
            );
          })}
        </div>

        {/* Audit log — surfaced on-demand so the dialog stays lean. */}
        <div className="mt-5 border-t pt-4">
          <button
            onClick={() => setAuditOpen(!auditOpen)}
            className="text-xs font-bold text-neutral-500 hover:text-neutral-800 flex items-center gap-1.5"
            data-testid={`fb-audit-toggle-${tenant.id}`}>
            {auditOpen ? "▼" : "▶"} Audit log ({audit.length || "load"})
          </button>
          {auditOpen && (
            <div className="mt-2 max-h-60 overflow-y-auto text-[11px] font-mono" data-testid={`fb-audit-list-${tenant.id}`}>
              {auditLoading && <div className="text-neutral-400">Loading…</div>}
              {!auditLoading && audit.length === 0 && (
                <div className="text-neutral-400">No BYO activity recorded yet.</div>
              )}
              {audit.map((e, i) => (
                <div key={i} className="py-1 border-b border-neutral-100 last:border-0">
                  <span className="text-neutral-400">{new Date(e.at).toLocaleString()}</span>
                  {" · "}<span className="font-bold">{e.action}</span>
                  {e.slot && <> · <span className="text-brand-sky">{e.slot}</span></>}
                  {" · "}<span className="text-neutral-500">{e.actor_role}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FirebaseSlotRow({ slotKey, slot, busy, tenantSlug, onUploaded, onClear }) {
  const [appId, setAppId] = useState(slot.app_id || "");
  const [identifier, setIdentifier] = useState(slot.identifier || "");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const platform = slotKey.endsWith("_android") ? "android" : "ios";
  const expectedFile = platform === "android" ? "google-services.json" : "GoogleService-Info.plist";

  const upload = async () => {
    if (!file) return toast.error(`Select the ${expectedFile}`);
    setSaving(true);
    try {
      const form = new FormData();
      // App ID and identifier are optional — the backend auto-parses them
      // from the file when blank. Still send them if the operator typed
      // values (backend validates that they match the file).
      if (appId.trim()) form.append("app_id", appId.trim());
      if (identifier.trim()) form.append("identifier", identifier.trim());
      form.append("config_file", file);
      await api.post(
        `/api/super-admin/firebase/tenant/${tenantSlug}/apps/${slotKey}/manual`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`${SLOT_LABELS[slotKey]} uploaded`);
      setFile(null);
      setExpanded(false);
      onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const badge = slot.state === "ok"
    ? (slot.source === "byo"
        ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">BYO</span>
        : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">AUTO</span>)
    : slot.state === "error"
      ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">ERROR</span>
      : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500">EMPTY</span>;

  return (
    <div className="border rounded-lg p-3" data-testid={`fb-slot-${slotKey}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{SLOT_LABELS[slotKey]}</span>
          {badge}
        </div>
        <div className="flex gap-1.5">
          {slot.state === "ok" && slot.source === "byo" && (
            <button onClick={onClear} disabled={busy}
                    className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                    data-testid={`fb-slot-clear-${slotKey}`}>
              Clear BYO
            </button>
          )}
          <button onClick={() => setExpanded(!expanded)}
                  className="text-xs px-2 py-1 rounded border border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                  data-testid={`fb-slot-toggle-${slotKey}`}>
            {slot.state === "empty" ? "Upload BYO" : (expanded ? "Cancel" : "Replace")}
          </button>
        </div>
      </div>
      {slot.state === "ok" && (
        <div className="mt-1 text-[11px] font-mono text-neutral-500 truncate">
          {slot.app_id} {slot.identifier ? `· ${slot.identifier}` : ""}
        </div>
      )}
      {expanded && (
        <div className="mt-2 space-y-2">
          <Input placeholder="App ID (e.g. 1:12345:android:abc)" value={appId}
                 onChange={(e) => setAppId(e.target.value)}
                 data-testid={`fb-slot-app-id-${slotKey}`}/>
          <Input placeholder={platform === "android"
                                ? "Package name (e.g. com.acme.customer)"
                                : "Bundle ID (e.g. com.acme.customer)"}
                 value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                 data-testid={`fb-slot-identifier-${slotKey}`}/>
          <input type="file"
                 accept={platform === "android" ? "application/json" : ".plist,text/xml,application/xml"}
                 onChange={(e) => setFile(e.target.files?.[0] || null)}
                 className="text-xs"
                 data-testid={`fb-slot-file-${slotKey}`}/>
          <div className="text-[11px] text-neutral-500">
            Expected file: <span className="font-mono">{expectedFile}</span>
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={upload} disabled={saving}
                    data-testid={`fb-slot-save-${slotKey}`}>
              {saving ? "Uploading…" : "Save BYO config"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}



function CustomPlanDialog({ open, onOpenChange, tenant, addons, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    // Wrap the form sync in an inner helper — keeps the linter's
    // set-state-in-effect heuristic from flagging this useEffect.
    const syncForm = () => {
      const cp = tenant.custom_plan;
      setForm({
        name: cp?.name || `${tenant.name} Custom`,
        description: cp?.description || "",
        monthly_price: cp?.monthly_price ?? 0,
        yearly_price: cp?.yearly_price ?? 0,
        billing_cycle: cp?.billing_cycle || "monthly",
        customer_limit: cp?.customer_limit ?? 0,
        delivery_limit_per_day: cp?.delivery_limit_per_day ?? 0,
        storage_limit_gb: cp?.storage_limit_gb ?? 0,
        overage_customers: cp?.overage_rates?.customers ?? 0,
        overage_deliveries: cp?.overage_rates?.deliveries ?? 0,
        overage_storage: cp?.overage_rates?.storage_gb ?? 0,
        included_addons: cp?.included_addons || [],
        notes: cp?.notes || "",
        activate_now: !cp,    // first save activates; subsequent edits leave renewal date alone
      });
    };
    syncForm();
  }, [tenant, open]);

  if (!tenant || !form) return null;
  const exists = !!tenant.custom_plan;

  const toggleAddon = (key) => {
    setForm((f) => ({
      ...f,
      included_addons: f.included_addons.includes(key)
        ? f.included_addons.filter((k) => k !== key)
        : [...f.included_addons, key],
    }));
  };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.put(`/api/super-admin/tenants/${tenant.id}/custom-plan`, {
        name: form.name,
        description: form.description,
        monthly_price: Number(form.monthly_price) || 0,
        yearly_price: Number(form.yearly_price) || 0,
        billing_cycle: form.billing_cycle,
        customer_limit: Number(form.customer_limit) || 0,
        delivery_limit_per_day: Number(form.delivery_limit_per_day) || 0,
        storage_limit_gb: Number(form.storage_limit_gb) || 0,
        overage_rates: {
          customers: Number(form.overage_customers) || 0,
          deliveries: Number(form.overage_deliveries) || 0,
          storage_gb: Number(form.overage_storage) || 0,
        },
        included_addons: form.included_addons,
        notes: form.notes,
        activate_now: form.activate_now,
      });
      toast.success(exists ? "Custom plan updated" : "Custom plan created & activated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove custom plan for ${tenant.name}? They'll need to pick a standard plan to keep service.`)) return;
    setBusy(true);
    try {
      await api.delete(`/api/super-admin/tenants/${tenant.id}/custom-plan`);
      toast.success("Custom plan removed"); onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-3xl max-h-[88vh] overflow-y-auto" data-testid="custom-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
            <Crown className="w-5 h-5 text-brand-clay"/>
            {exists ? "Edit" : "Negotiate"} Custom Plan — {tenant.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-3">
          {/* Identity */}
          <div className="space-y-3">
            <Field label="Plan Name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="h-11 rounded-2xl" data-testid="cp-name"/>
            </Field>
            <Field label="Description (shown to tenant)">
              <textarea value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="w-full min-h-[64px] rounded-2xl border border-neutral-200 px-4 py-2.5 text-sm"
                        placeholder="e.g., Bespoke plan for premium dairies — includes priority support"
                        data-testid="cp-description"/>
            </Field>
          </div>

          {/* Pricing */}
          <section className="rounded-2xl bg-brand-cream p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider font-bold text-neutral-600">Pricing</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Monthly Price (₹)">
                <Input type="number" value={form.monthly_price}
                       onChange={(e) => setForm({ ...form, monthly_price: e.target.value })}
                       className="h-11 rounded-2xl font-mono" data-testid="cp-monthly"/>
              </Field>
              <Field label="Yearly Price (₹)">
                <Input type="number" value={form.yearly_price}
                       onChange={(e) => setForm({ ...form, yearly_price: e.target.value })}
                       className="h-11 rounded-2xl font-mono" data-testid="cp-yearly"/>
              </Field>
            </div>
            <Field label="Billing Cycle">
              <div className="grid grid-cols-2 gap-2">
                {["monthly", "yearly"].map((c) => (
                  <button key={c} type="button"
                          onClick={() => setForm({ ...form, billing_cycle: c })}
                          className={`h-11 rounded-2xl font-bold text-sm border-2 capitalize ${
                            form.billing_cycle === c
                              ? "border-brand-sky bg-brand-sky/10 text-brand-sky"
                              : "border-neutral-200 bg-white"
                          }`}
                          data-testid={`cp-cycle-${c}`}>
                    {c}
                  </button>
                ))}
              </div>
            </Field>
          </section>

          {/* Limits */}
          <section className="rounded-2xl bg-brand-cream p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider font-bold text-neutral-600">Usage Limits <span className="text-neutral-400 normal-case">(0 = unlimited)</span></div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Customers">
                <Input type="number" value={form.customer_limit}
                       onChange={(e) => setForm({ ...form, customer_limit: e.target.value })}
                       className="h-11 rounded-2xl font-mono" data-testid="cp-limit-customers"/>
              </Field>
              <Field label="Deliveries/day">
                <Input type="number" value={form.delivery_limit_per_day}
                       onChange={(e) => setForm({ ...form, delivery_limit_per_day: e.target.value })}
                       className="h-11 rounded-2xl font-mono" data-testid="cp-limit-deliveries"/>
              </Field>
              <Field label="Storage (GB)">
                <Input type="number" value={form.storage_limit_gb}
                       onChange={(e) => setForm({ ...form, storage_limit_gb: e.target.value })}
                       className="h-11 rounded-2xl font-mono" data-testid="cp-limit-storage"/>
              </Field>
            </div>
          </section>

          {/* Overage rates */}
          <section className="rounded-2xl bg-brand-cream p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider font-bold text-neutral-600">Overage Rates (₹ per extra unit)</div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Per Customer">
                <Input type="number" step="0.01" value={form.overage_customers}
                       onChange={(e) => setForm({ ...form, overage_customers: e.target.value })}
                       className="h-11 rounded-2xl font-mono" data-testid="cp-over-customers"/>
              </Field>
              <Field label="Per Delivery">
                <Input type="number" step="0.01" value={form.overage_deliveries}
                       onChange={(e) => setForm({ ...form, overage_deliveries: e.target.value })}
                       className="h-11 rounded-2xl font-mono" data-testid="cp-over-deliveries"/>
              </Field>
              <Field label="Per GB/day">
                <Input type="number" step="0.01" value={form.overage_storage}
                       onChange={(e) => setForm({ ...form, overage_storage: e.target.value })}
                       className="h-11 rounded-2xl font-mono" data-testid="cp-over-storage"/>
              </Field>
            </div>
          </section>

          {/* Included addons */}
          <section className="rounded-2xl bg-brand-cream p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider font-bold text-neutral-600">Included Addons <span className="text-neutral-400 normal-case">(bundled — not billed separately)</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {addons.map((a) => {
                const checked = form.included_addons.includes(a.key);
                return (
                  <label key={a.key}
                         className={`flex items-start gap-2 p-3 rounded-2xl bg-white border-2 cursor-pointer ${
                           checked ? "border-brand-leaf" : "border-neutral-200"
                         }`}
                         data-testid={`cp-addon-${a.key}`}>
                    <input type="checkbox" checked={checked}
                           onChange={() => toggleAddon(a.key)}
                           className="mt-0.5 accent-brand-leaf"/>
                    <div className="flex-1">
                      <div className="font-bold text-sm">{a.name}</div>
                      <div className="text-[11px] text-neutral-500">{formatINR(a.monthly_price)}/mo · {a.description?.slice(0, 60)}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </section>

          {/* Notes */}
          <Field label="Negotiation Notes (internal)">
            <textarea value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="w-full min-h-[60px] rounded-2xl border border-neutral-200 px-4 py-2.5 text-sm"
                      placeholder="e.g., Locked-in pricing for 12 months per Feb 2026 CEO call"
                      data-testid="cp-notes"/>
          </Field>

          {/* Activate toggle */}
          <label className="flex items-center justify-between p-3 rounded-2xl bg-brand-sand/10 border border-brand-sand/30">
            <div>
              <div className="font-bold text-sm">Activate immediately</div>
              <div className="text-xs text-neutral-600">
                Reset renewal date to today + {form.billing_cycle === "yearly" ? "365" : "30"} days and enable included addons.
              </div>
            </div>
            <Switch checked={form.activate_now}
                    onCheckedChange={(v) => setForm({ ...form, activate_now: v })}
                    data-testid="cp-activate-now"/>
          </label>

          <div className="flex gap-2 pt-2">
            <Button onClick={save} disabled={busy}
                    className="flex-1 h-12 rounded-2xl bg-brand-clay hover:bg-orange-700 font-bold"
                    data-testid="cp-save">
              {busy ? "Saving…" : (exists ? "Update Custom Plan" : "Create & Activate")}
            </Button>
            {exists && (
              <Button onClick={remove} disabled={busy} variant="outline"
                      className="h-12 rounded-2xl border-2 border-brand-clay/40 text-brand-clay hover:bg-brand-clay/10 font-bold"
                      data-testid="cp-delete">
                <Trash2 className="w-4 h-4 mr-1"/> Remove
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}



function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">{label}</div>
      <div className="font-heading font-bold text-sm mt-0.5">{value}</div>
    </div>
  );
}

export function SuperPlans() {
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [edit, setEdit] = useState({});      // {plan_id: {monthly_price, yearly_price}}
  const [editAddon, setEditAddon] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [pRes, aRes] = await Promise.all([
      api.get("/api/super-admin/plans"),
      api.get("/api/super-admin/addons"),
    ]);
    setPlans(pRes.data.plans);
    setAddons(aRes.data.addons);
    setEdit(Object.fromEntries(pRes.data.plans.map((p) => [p.id, {
      monthly_price: p.monthly_price,
      yearly_price: p.yearly_price || 0,
      description: p.description || "",
      customer_limit: p.customer_limit,
      delivery_limit_per_day: p.delivery_limit_per_day,
      storage_limit_gb: p.storage_limit_gb || 1,
      included_modules: p.included_modules || [],
      overage_rates: {
        customers: (p.overage_rates || {}).customers || 0,
        deliveries: (p.overage_rates || {}).deliveries || 0,
        storage_gb: (p.overage_rates || {}).storage_gb || 0,
      },
    }])));
    setEditAddon(Object.fromEntries(aRes.data.addons.map((a) => [a.key, { monthly_price: a.monthly_price, yearly_price: a.yearly_price || 0 }])));
  };
  useEffect(() => {
    // Effect-only initial load, kept inline so `load` (which is also called
    // from click handlers) doesn't trigger react-hooks/set-state-in-effect.
    const initialLoad = async () => {
      const [pRes, aRes] = await Promise.all([
        api.get("/api/super-admin/plans"),
        api.get("/api/super-admin/addons"),
      ]);
      setPlans(pRes.data.plans);
      setAddons(aRes.data.addons);
      setEdit(Object.fromEntries(pRes.data.plans.map((p) => [p.id, {
        monthly_price: p.monthly_price,
        yearly_price: p.yearly_price || 0,
        description: p.description || "",
        customer_limit: p.customer_limit,
        delivery_limit_per_day: p.delivery_limit_per_day,
        storage_limit_gb: p.storage_limit_gb || 1,
        included_modules: p.included_modules || [],
        overage_rates: {
          customers: (p.overage_rates || {}).customers || 0,
          deliveries: (p.overage_rates || {}).deliveries || 0,
          storage_gb: (p.overage_rates || {}).storage_gb || 0,
        },
      }])));
      setEditAddon(Object.fromEntries(aRes.data.addons.map((a) => [a.key, { monthly_price: a.monthly_price, yearly_price: a.yearly_price || 0 }])));
    };
    initialLoad();
  }, []);

  const savePlan = async (id) => {
    setBusy(true);
    try {
      const e = edit[id];
      await api.patch(`/api/super-admin/plans/${id}`, {
        monthly_price: parseFloat(e.monthly_price),
        yearly_price: parseFloat(e.yearly_price),
        description: e.description,
        customer_limit: parseInt(e.customer_limit),
        delivery_limit_per_day: parseInt(e.delivery_limit_per_day),
        storage_limit_gb: parseFloat(e.storage_limit_gb),
        included_modules: e.included_modules,
        overage_rates: {
          customers: parseFloat(e.overage_rates.customers) || 0,
          deliveries: parseFloat(e.overage_rates.deliveries) || 0,
          storage_gb: parseFloat(e.overage_rates.storage_gb) || 0,
        },
      });
      toast.success("Plan saved");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggleModuleIncluded = (planId, key) => {
    setEdit((cur) => {
      const e = cur[planId] || {};
      const mods = new Set(e.included_modules || []);
      if (mods.has(key)) mods.delete(key); else mods.add(key);
      return { ...cur, [planId]: { ...e, included_modules: Array.from(mods) } };
    });
  };

  const saveAddon = async (key) => {
    setBusy(true);
    try {
      const e = editAddon[key];
      await api.patch(`/api/super-admin/addons/${key}`, {
        monthly_price: parseFloat(e.monthly_price), yearly_price: parseFloat(e.yearly_price),
      });
      toast.success("Addon prices saved");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggleSelfServe = async (key, on) => {
    setBusy(true);
    try {
      await api.patch(`/api/super-admin/addons/${key}`, { is_self_serve: on });
      toast.success(on ? "Addon now self-serve" : "Addon hidden from tenants");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const runRenewals = async () => {
    if (!window.confirm("Run the daily renewal job NOW? This will debit any overdue plan or addon renewals immediately.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/super-admin/run-renewals");
      const s = data.summary;
      toast.success(`Renewals done · ${s.plans_renewed} plans + ${s.addons_renewed} addons renewed · ${formatINR(s.plans_revenue + s.addons_revenue)} debited`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 page-enter" data-testid="super-plans-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Pricing</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Plans & Add-on Modules</h1>
          <p className="text-sm text-neutral-600 mt-1">Edit monthly + yearly prices. Renewals run daily at 02:00 UTC.</p>
        </div>
        <Button onClick={runRenewals} disabled={busy}
                className="h-11 rounded-2xl bg-brand-ink text-white font-bold"
                data-testid="run-renewals-btn">
          <Sparkles className="w-4 h-4 mr-1.5"/> Run renewals now
        </Button>
      </div>

      <div data-testid="super-plans-section">
        <h3 className="font-heading font-bold mb-3">SaaS Plans</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {plans.map((p) => {
            const e = edit[p.id];
            if (!e) return null;
            const yearlySave = e.monthly_price * 12 - e.yearly_price;
            return (
              <div key={p.id} className="bg-white rounded-3xl border border-neutral-200 p-5 space-y-3" data-testid={`plan-${p.name.toLowerCase()}`}>
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-brand-sky">{p.name}</div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Description</label>
                  <textarea value={e.description} rows={3}
                    onChange={(ev) => setEdit({ ...edit, [p.id]: { ...e, description: ev.target.value } })}
                    className="w-full rounded-2xl mt-1 border border-neutral-200 px-3 py-2 text-sm"
                    data-testid={`plan-description-${p.name.toLowerCase()}`}/>
                </div>

                {/* Pricing */}
                <div className="grid grid-cols-2 gap-2 pt-3 border-t border-neutral-100">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Monthly ₹</label>
                    <Input type="number" value={e.monthly_price}
                      onChange={(ev) => setEdit({ ...edit, [p.id]: { ...e, monthly_price: ev.target.value } })}
                      className="h-10 rounded-2xl mt-0.5 font-heading font-bold"
                      data-testid={`plan-monthly-${p.name.toLowerCase()}`}/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Yearly ₹</label>
                    <Input type="number" value={e.yearly_price}
                      onChange={(ev) => setEdit({ ...edit, [p.id]: { ...e, yearly_price: ev.target.value } })}
                      className="h-10 rounded-2xl mt-0.5 font-heading font-bold"
                      data-testid={`plan-yearly-${p.name.toLowerCase()}`}/>
                  </div>
                </div>
                {e.yearly_price > 0 && yearlySave > 0 && (
                  <div className="text-xs text-brand-leaf font-bold">≈ {formatINR(Math.round(e.yearly_price / 12))}/mo · Save {formatINR(yearlySave)}/yr</div>
                )}

                {/* Usage limits */}
                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-neutral-100">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Customers</label>
                    <Input type="number" value={e.customer_limit}
                      onChange={(ev) => setEdit({ ...edit, [p.id]: { ...e, customer_limit: ev.target.value } })}
                      className="h-10 rounded-2xl mt-0.5 font-bold text-sm"
                      data-testid={`plan-customer-limit-${p.name.toLowerCase()}`}/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Deliv/day</label>
                    <Input type="number" value={e.delivery_limit_per_day}
                      onChange={(ev) => setEdit({ ...edit, [p.id]: { ...e, delivery_limit_per_day: ev.target.value } })}
                      className="h-10 rounded-2xl mt-0.5 font-bold text-sm"
                      data-testid={`plan-delivery-limit-${p.name.toLowerCase()}`}/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">GB</label>
                    <Input type="number" step="0.1" value={e.storage_limit_gb}
                      onChange={(ev) => setEdit({ ...edit, [p.id]: { ...e, storage_limit_gb: ev.target.value } })}
                      className="h-10 rounded-2xl mt-0.5 font-bold text-sm"
                      data-testid={`plan-storage-limit-${p.name.toLowerCase()}`}/>
                  </div>
                </div>

                {/* Overage rates */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Overage ₹ / unit-over-limit / day</label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <Input type="number" step="0.5" value={e.overage_rates.customers}
                      onChange={(ev) => setEdit({ ...edit, [p.id]: { ...e, overage_rates: { ...e.overage_rates, customers: ev.target.value } } })}
                      placeholder="per customer"
                      className="h-10 rounded-2xl font-bold text-sm"
                      data-testid={`plan-overage-customers-${p.name.toLowerCase()}`}/>
                    <Input type="number" step="0.5" value={e.overage_rates.deliveries}
                      onChange={(ev) => setEdit({ ...edit, [p.id]: { ...e, overage_rates: { ...e.overage_rates, deliveries: ev.target.value } } })}
                      placeholder="per delivery"
                      className="h-10 rounded-2xl font-bold text-sm"
                      data-testid={`plan-overage-deliveries-${p.name.toLowerCase()}`}/>
                    <Input type="number" step="0.5" value={e.overage_rates.storage_gb}
                      onChange={(ev) => setEdit({ ...edit, [p.id]: { ...e, overage_rates: { ...e.overage_rates, storage_gb: ev.target.value } } })}
                      placeholder="per GB"
                      className="h-10 rounded-2xl font-bold text-sm"
                      data-testid={`plan-overage-storage-${p.name.toLowerCase()}`}/>
                  </div>
                </div>

                {/* Included addons (multi-select) */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Included addons (free with this plan)</label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {addons.map((a) => {
                      const on = (e.included_modules || []).includes(a.key);
                      return (
                        <button key={a.key} onClick={() => toggleModuleIncluded(p.id, a.key)}
                          className={`px-2.5 h-8 rounded-full text-xs font-bold border-2 ${on ? "bg-brand-leaf text-white border-brand-leaf" : "bg-white text-neutral-700 border-neutral-200"}`}
                          data-testid={`plan-addon-${p.name.toLowerCase()}-${a.key}`}>
                          {on ? "✓ " : "+ "}{a.name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <Button onClick={() => savePlan(p.id)} disabled={busy}
                  className="w-full h-11 rounded-2xl bg-brand-ink text-white font-bold mt-3"
                  data-testid={`save-plan-${p.name.toLowerCase()}`}>
                  Save changes
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div data-testid="super-addons-section">
        <h3 className="font-heading font-bold mb-3">Add-on Modules ({addons.length})</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {addons.map((a) => {
            const e = editAddon[a.key] || { monthly_price: a.monthly_price, yearly_price: a.yearly_price || 0 };
            const selfServe = a.is_self_serve !== false;
            return (
              <div key={a.key} className={`bg-white rounded-3xl border p-5 space-y-2 ${selfServe ? "border-neutral-200" : "border-brand-sand/50 bg-brand-sand/5"}`} data-testid={`addon-card-${a.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold">{a.name}</div>
                    <div className="text-xs text-neutral-600 mb-1 line-clamp-2">{a.description}</div>
                  </div>
                  {!selfServe && (
                    <span className="px-2 py-0.5 rounded-full bg-brand-sand/30 text-brand-sand text-[10px] font-extrabold uppercase tracking-wider shrink-0" data-testid={`platform-managed-${a.key}`}>
                      Platform-only
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Monthly ₹</label>
                    <Input type="number" value={e.monthly_price}
                           onChange={(ev) => setEditAddon({ ...editAddon, [a.key]: { ...e, monthly_price: ev.target.value } })}
                           className="h-10 rounded-2xl mt-0.5 font-bold text-sm"
                           data-testid={`addon-monthly-${a.key}`}/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Yearly ₹</label>
                    <Input type="number" value={e.yearly_price}
                           onChange={(ev) => setEditAddon({ ...editAddon, [a.key]: { ...e, yearly_price: ev.target.value } })}
                           className="h-10 rounded-2xl mt-0.5 font-bold text-sm"
                           data-testid={`addon-yearly-${a.key}`}/>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs font-bold text-neutral-700 select-none cursor-pointer pt-1">
                  <input type="checkbox" checked={selfServe}
                         onChange={(ev) => toggleSelfServe(a.key, ev.target.checked)}
                         className="w-4 h-4 rounded accent-brand-leaf"
                         data-testid={`self-serve-toggle-${a.key}`}/>
                  Tenants can self-subscribe
                  <span className="text-neutral-400 font-normal ml-auto">{selfServe ? "(visible to tenants)" : "(hidden · manual grant only)"}</span>
                </label>
                <Button onClick={() => saveAddon(a.key)} disabled={busy}
                        className="w-full h-10 rounded-2xl bg-brand-sky text-white font-bold text-sm"
                        data-testid={`save-addon-${a.key}`}>
                  Save prices
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// Destructive, Super Admin–only. The admin picks WHICH categories to clear —
// orders, subscriptions, wallets, customers, routes, riders — and only those
// are touched. Catalog (products/categories/brands), banners, apartments and
// every tenant setting are never clearable from here.
//
// Selecting "customers" cascades to that customer's OWN records (their
// orders, subscriptions, wallet, ledger, devices), because a delivery whose
// customer no longer exists is an orphan that still shows in reports and
// rider queues with a blank name. The cascade counts are shown up front so
// it is never a surprise. See backend/routes_super_admin.py _run_clear().
//
// Two-step confirm: step 1 shows real counts fetched from the backend and
// what will/won't be touched; step 2 requires typing the tenant's slug
// before the delete button enables, so a slip of a click can't trigger it.
// One row per clearable category. Order and wording mirror the backend's
// CLEAR_SCOPES so the dialog can never offer something the server won't do.
const SCOPE_ROWS = [
  { key: "customers", label: "Customers",
    detail: "Customer accounts, and all of their own records (see warning below)." },
  { key: "wallets", label: "Customer wallets / balances",
    detail: "Wallet balances and the full transaction ledger behind them, including positive balances." },
  { key: "subscriptions", label: "Subscriptions",
    detail: "Every subscription for this tenant, any status." },
  { key: "orders", label: "Orders / deliveries",
    detail: "Deliveries, invoices and pending checkout attempts." },
  { key: "routes", label: "Routes",
    detail: "Delivery routes. Orders keep their history and become unassigned." },
  { key: "riders", label: "Riders / delivery boys",
    detail: "Rider accounts and their devices. Orders keep their history and become unassigned." },
  { key: "otp_wallet", label: "Platform OTP wallet",
    detail: "The tenant's platform billing wallet: clears the OTP/bill-share charge ledger and resets the dashboard balance to ₹0. Not a customer wallet." },
  { key: "reports", label: "Reports / report data",
    detail: "Reports are generated live — there is no report table. This clears the records they read (deliveries + invoices), the same ones Orders / deliveries covers." },
];

function ClearOrdersDialog({ tenant, onClose }) {
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  // Explicit opt-in selection — nothing pre-checked. There's no reliable
  // signal anywhere in the data to auto-detect "test" vs a real paying
  // customer's subscription, so every permanent deletion must be a
  // deliberate, individually-reviewed choice rather than an inferred bulk
  // action.
  const [selectedSubIds, setSelectedSubIds] = useState([]);
  // Nothing pre-checked, for the same reason the subscription list isn't:
  // every permanent deletion must be a deliberate choice, never a default.
  const [scopes, setScopes] = useState([]);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setStep(1); setPreview(null); setPreviewError(null);
    setConfirmText(""); setResult(null); setSelectedSubIds([]); setScopes([]);
    if (!tenant) return;
    setLoadingPreview(true);
    api.get(`/api/super-admin/tenants/${tenant.id}/clear-orders/preview`)
      .then(({ data }) => setPreview(data))
      .catch((e) => setPreviewError(e.response?.data?.detail || "Couldn't load preview"))
      .finally(() => setLoadingPreview(false));
  }, [tenant?.id]);

  const toggleSub = (id) => {
    setSelectedSubIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };
  const selectAllSubs = () => setSelectedSubIds((preview?.subscriptions || []).map((s) => s.id));
  const selectNoneSubs = () => setSelectedSubIds([]);
  const toggleScope = (k) =>
    setScopes((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);

  const doClear = async () => {
    if (!tenant) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/super-admin/tenants/${tenant.id}/clear-orders`, {
        confirm_slug: confirmText.trim(),
        subscription_ids_to_delete: selectedSubIds,
        scopes,
      });
      setResult(data);
      toast.success("Selected data cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(false);
    }
  };

  const slugMatches = tenant && confirmText.trim().toLowerCase() === tenant.slug.toLowerCase();
  const subs = preview?.subscriptions || [];

  return (
    <Dialog open={!!tenant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="clear-orders-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-brand-clay">Clear Tenant Data</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="rounded-2xl bg-brand-leaf/10 border border-brand-leaf/30 p-4 text-sm space-y-1" data-testid="clear-orders-result">
              <div className="font-bold text-brand-leaf mb-1">Done — {tenant?.name} is clear</div>
              <div>Orders deleted: <b>{result.orders_deleted}</b></div>
              <div>Invoices deleted: <b>{result.invoices_deleted}</b></div>
              <div>Pending checkouts deleted: <b>{result.pending_checkouts_deleted}</b></div>
              <div>Subscriptions deleted: <b>{result.subscriptions_deleted}</b></div>
            </div>
            <Button onClick={onClose} className="w-full h-11 rounded-2xl bg-brand-ink text-white font-bold" data-testid="clear-orders-close">
              Close
            </Button>
          </div>
        ) : loadingPreview ? (
          <LoadingState />
        ) : previewError ? (
          <EmptyState icon={AlertTriangle} title="Couldn't load preview" description={previewError} size="sm" />
        ) : !preview ? null : step === 1 ? (
          <div className="space-y-4">
            <p className="text-sm text-neutral-700">
              For <b>{tenant?.name}</b> ({tenant?.slug}), choose what to
              <b> permanently delete</b>. Nothing is selected by default.
            </p>
            <div className="space-y-2" data-testid="clear-orders-preview">
              {SCOPE_ROWS.map(({ key, label, detail }) => {
                const counts = preview.scope_counts?.[key] || {};
                const total = Object.values(counts).reduce((a, b) => a + (b || 0), 0);
                return (
                  <label key={key}
                         className={`flex items-start gap-2.5 p-3 rounded-2xl border-2 cursor-pointer transition-colors ${
                           scopes.includes(key) ? "border-brand-clay/40 bg-brand-clay/5" : "border-neutral-200 bg-white"
                         }`}
                         data-testid={`scope-${key}`}>
                    <input type="checkbox" checked={scopes.includes(key)}
                           onChange={() => toggleScope(key)} className="mt-1"
                           data-testid={`scope-input-${key}`}/>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm flex items-center gap-2 flex-wrap">
                        {label}
                        <span className="text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-neutral-200 text-neutral-700">
                          {total} record{total === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="text-[11px] text-neutral-600 mt-0.5">{detail}</div>
                      <div className="text-[11px] text-neutral-500 mt-1 flex flex-wrap gap-x-3">
                        {Object.entries(counts).map(([k, v]) => (
                          <span key={k}>{k.replace(/_/g, " ")}: <b>{v}</b></span>
                        ))}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            {scopes.includes("customers") && (
              <div className="rounded-2xl bg-brand-clay/10 border border-brand-clay/40 p-3 text-xs text-brand-clay font-bold"
                   data-testid="clear-cascade-warning">
                Clearing customers also deletes their own orders, subscriptions, wallets,
                transactions, invoices, loyalty and registered devices — otherwise those
                rows would be left without an owner.
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-sm font-bold">
                  Subscriptions ({subs.length})
                </div>
                {subs.length > 0 && (
                  <div className="flex gap-2 text-xs font-bold">
                    <button onClick={selectAllSubs} className="text-brand-sky" data-testid="select-all-subs">Select all</button>
                    <button onClick={selectNoneSubs} className="text-neutral-400" data-testid="select-none-subs">Clear</button>
                  </div>
                )}
              </div>
              {subs.length === 0 ? (
                <div className="text-sm text-neutral-500 rounded-2xl bg-neutral-50 border border-neutral-200 p-4">
                  No subscriptions for this tenant.
                </div>
              ) : (
                <>
                  <p className="text-xs text-neutral-600 mb-2">
                    {scopes.includes("subscriptions")
                      ? "The Subscriptions category above is selected, so ALL of these will be deleted — this per-row list is ignored."
                      : "Nothing is selected by default."} Check only the ones you know are test data —
                    selected subscriptions are <b>permanently deleted</b>, not just cancelled. Includes
                    subscriptions of any status (active, paused, or already-cancelled).
                  </p>
                  <div className="max-h-64 overflow-y-auto rounded-2xl border border-neutral-200 divide-y divide-neutral-100" data-testid="subscription-checklist">
                    {subs.map((s) => (
                      <label key={s.id} className="flex items-start gap-2.5 p-3 text-sm cursor-pointer hover:bg-neutral-50" data-testid={`sub-row-${s.id}`}>
                        <input type="checkbox" checked={selectedSubIds.includes(s.id)} onChange={() => toggleSub(s.id)} className="mt-1"/>
                        <div className="min-w-0 flex-1">
                          <div className="font-bold truncate flex items-center gap-1.5 flex-wrap">
                            {s.customer_name} <span className="font-normal text-neutral-500">· {s.customer_phone}</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider ${
                              s.status === "active" ? "bg-brand-leaf/15 text-brand-leaf" : "bg-neutral-200 text-neutral-600"
                            }`}>{s.status}</span>
                          </div>
                          <div className="text-neutral-600 truncate">
                            {s.product_name} × {s.quantity} · {s.frequency} · {formatINR(s.price_per_unit)}/delivery
                          </div>
                          <div className="text-[11px] text-neutral-400">
                            Started {s.start_date} · created {(s.created_at || "").slice(0, 10)}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="rounded-2xl bg-brand-leaf/5 border border-brand-leaf/20 p-4 text-xs text-neutral-600" data-testid="clear-orders-safe-list">
              <b>Never touched:</b> products, categories, brands, banners, apartments, service
              areas and every tenant setting. Any category left unchecked above stays exactly
              as-is. Clearing routes or riders does not delete orders — those orders simply
              become unassigned.
              {preview.protected_accounts_kept > 0 && (
                <div className="mt-2 font-bold" data-testid="clear-protected-note">
                  {preview.protected_accounts_kept} protected account
                  {preview.protected_accounts_kept === 1 ? "" : "s"} (App Store reviewer,
                  test admin, super admin) are excluded and will survive — enforced by the
                  backend, not just hidden here.
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1 h-11 rounded-2xl" data-testid="clear-orders-cancel">
                Cancel
              </Button>
              <Button onClick={() => setStep(2)}
                      disabled={scopes.length === 0 && selectedSubIds.length === 0}
                      className="flex-1 h-11 rounded-2xl bg-brand-clay text-white font-bold disabled:opacity-40"
                      data-testid="clear-orders-continue">
                Continue
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl bg-brand-clay/10 border border-brand-clay/40 p-4 text-sm text-brand-clay font-bold" data-testid="clear-orders-warning">
              This is permanent and cannot be undone.
            </div>
            <div className="rounded-2xl bg-neutral-50 border border-neutral-200 p-4 text-sm space-y-1"
                 data-testid="clear-orders-selected-summary">
              <div className="font-bold mb-1">Selected for deletion</div>
              {scopes.length === 0 && selectedSubIds.length === 0 && (
                <div className="text-neutral-500">Nothing selected.</div>
              )}
              {SCOPE_ROWS.filter(({ key }) => scopes.includes(key)).map(({ key, label }) => {
                const counts = preview.scope_counts?.[key] || {};
                const total = Object.values(counts).reduce((a, b) => a + (b || 0), 0);
                return (
                  <div key={key} data-testid={`selected-${key}`}>
                    {label}: <b>{total}</b> record{total === 1 ? "" : "s"}
                  </div>
                );
              })}
              {!scopes.includes("subscriptions") && selectedSubIds.length > 0 && (
                <div>Individually selected subscriptions: <b>{selectedSubIds.length}</b></div>
              )}
            </div>
            <p className="text-sm text-neutral-700">
              Type <code className="font-mono font-bold">{tenant?.slug}</code> to confirm for <b>{tenant?.name}</b>.
            </p>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                   placeholder={tenant?.slug} className="h-12 rounded-2xl font-mono" data-testid="clear-orders-confirm-input"/>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-11 rounded-2xl" data-testid="clear-orders-back">
                Back
              </Button>
              <Button onClick={doClear} disabled={busy || !slugMatches}
                      className="flex-1 h-11 rounded-2xl bg-brand-clay text-white font-bold disabled:opacity-40"
                      data-testid="clear-orders-confirm-btn">
                {busy ? "Deleting…" : "Permanently Delete"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function LegalUrlsDialog({ tenant, onClose }) {
  // Origin used by Google Play & Apple App Store listings — must be an
  // absolute, publicly reachable URL. We resolve it from REACT_APP_BACKEND_URL
  // (which is the production HTTPS host in deployed builds) and fall back to
  // window.location.origin for preview/dev environments.
  const origin = (process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
  const slug = tenant?.slug || "";

  // Editable overrides — surfaced on the legal pages + native store
  // listings. Super Admin–only because Play Store reviewers cross-check
  // these against the developer's submitted listing.
  const [form, setForm] = useState({
    customer_app_name: "",
    rider_app_name: "",
    legal_entity_name: "",
    gstin: "",
    android_customer_min_version: "",
    ios_customer_min_version: "",
    android_rider_min_version: "",
    ios_rider_min_version: "",
    ios_customer_store_url: "",
    ios_rider_store_url: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    setForm({
      customer_app_name: tenant.customer_app_name || "",
      rider_app_name: tenant.rider_app_name || "",
      legal_entity_name: tenant.legal_entity_name || "",
      gstin: tenant.gstin || "",
      android_customer_min_version: tenant.android_customer_min_version || "",
      ios_customer_min_version: tenant.ios_customer_min_version || "",
      android_rider_min_version: tenant.android_rider_min_version || "",
      ios_rider_min_version: tenant.ios_rider_min_version || "",
      ios_customer_store_url: tenant.ios_customer_store_url || "",
      ios_rider_store_url: tenant.ios_rider_store_url || "",
    });
  }, [tenant?.id]);

  const saveNames = async () => {
    if (!tenant) return;
    setSaving(true);
    try {
      await api.patch(`/api/super-admin/tenants/${tenant.id}/branding`, form);
      toast.success("Saved");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const rows = tenant ? [
    { key: "customer-privacy", label: "Customer App · Privacy Policy", url: `${origin}/api/legal/customer/${slug}/privacy` },
    { key: "customer-terms",   label: "Customer App · Terms of Service", url: `${origin}/api/legal/customer/${slug}/terms` },
    { key: "rider-privacy",    label: "Rider App · Privacy Policy",    url: `${origin}/api/legal/rider/${slug}/privacy` },
    { key: "rider-terms",      label: "Rider App · Terms of Service",  url: `${origin}/api/legal/rider/${slug}/terms` },
  ] : [];
  const copy = async (url, label) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };
  return (
    <Dialog open={!!tenant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="legal-urls-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{tenant?.name} — App identity &amp; Legal</DialogTitle>
        </DialogHeader>

        {/* Editable app identity — appears on every legal page */}
        <div className="rounded-2xl border border-brand-sand/40 bg-brand-sand/5 p-4 space-y-3" data-testid="app-identity-editor">
          <div className="text-xs uppercase tracking-wider font-bold text-brand-clay">App identity (Play Store / App Store)</div>
          <p className="text-xs text-neutral-600 -mt-1">
            These names appear on the legal pages above &amp; must match the title
            submitted on Google Play / App Store Connect for each app. Override the
            auto-generated <code>&quot;{tenant?.name} Customer App&quot;</code> /
            <code> &quot;{tenant?.name} Rider App&quot;</code> defaults if needed.
          </p>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Customer app name</label>
              <Input value={form.customer_app_name}
                     onChange={(e) => setForm({ ...form, customer_app_name: e.target.value })}
                     placeholder={`${tenant?.name || "Brand"} Customer App`}
                     className="h-10 rounded-2xl" data-testid="super-customer-app-name"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Rider app name</label>
              <Input value={form.rider_app_name}
                     onChange={(e) => setForm({ ...form, rider_app_name: e.target.value })}
                     placeholder={`${tenant?.name || "Brand"} Rider App`}
                     className="h-10 rounded-2xl" data-testid="super-rider-app-name"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Legal entity / Registered company</label>
              <Input value={form.legal_entity_name}
                     onChange={(e) => setForm({ ...form, legal_entity_name: e.target.value })}
                     placeholder={tenant?.name || "ABC Dairy Pvt Ltd"}
                     className="h-10 rounded-2xl" data-testid="super-legal-entity-name"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">GSTIN (optional)</label>
              <Input value={form.gstin}
                     onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                     placeholder="29ABCDE1234F1Z5"
                     className="h-10 rounded-2xl font-mono uppercase" data-testid="super-gstin"/>
            </div>
          </div>
          <Button onClick={saveNames} disabled={saving}
                  className="w-full h-11 rounded-2xl bg-brand-ink text-white font-bold"
                  data-testid="super-save-app-identity">
            {saving ? "Saving…" : "Save app identity"}
          </Button>
        </div>

        {/* Force Update — per-tenant minimum supported native app version.
            Empty = no minimum enforced (default, no behavior change). Set a
            version to require anyone below it to update before continuing —
            see frontend/src/lib/forceUpdate.js for the runtime check. */}
        <div className="rounded-2xl border border-brand-clay/30 bg-brand-clay/5 p-4 space-y-3 mt-4" data-testid="force-update-editor">
          <div className="text-xs uppercase tracking-wider font-bold text-brand-clay">Force update (minimum app version)</div>
          <p className="text-xs text-neutral-600 -mt-1">
            Leave a field blank to never force-update that app. When set, anyone on a lower
            version sees a mandatory update screen and can't continue until they update.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Android · Customer</label>
              <Input value={form.android_customer_min_version}
                     onChange={(e) => setForm({ ...form, android_customer_min_version: e.target.value })}
                     placeholder="e.g. 1.4.0" className="h-10 rounded-2xl font-mono" data-testid="super-min-ver-android-customer"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">iOS · Customer</label>
              <Input value={form.ios_customer_min_version}
                     onChange={(e) => setForm({ ...form, ios_customer_min_version: e.target.value })}
                     placeholder="e.g. 1.4.0" className="h-10 rounded-2xl font-mono" data-testid="super-min-ver-ios-customer"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Android · Rider</label>
              <Input value={form.android_rider_min_version}
                     onChange={(e) => setForm({ ...form, android_rider_min_version: e.target.value })}
                     placeholder="e.g. 1.4.0" className="h-10 rounded-2xl font-mono" data-testid="super-min-ver-android-rider"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">iOS · Rider</label>
              <Input value={form.ios_rider_min_version}
                     onChange={(e) => setForm({ ...form, ios_rider_min_version: e.target.value })}
                     placeholder="e.g. 1.4.0" className="h-10 rounded-2xl font-mono" data-testid="super-min-ver-ios-rider"/>
            </div>
          </div>
          <p className="text-xs text-neutral-600">
            "Update Now" opens the Play Store automatically for Android. iOS needs the App
            Store Connect listing URL for each app once published:
          </p>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">iOS Customer App Store URL</label>
              <Input value={form.ios_customer_store_url}
                     onChange={(e) => setForm({ ...form, ios_customer_store_url: e.target.value })}
                     placeholder="https://apps.apple.com/app/id..." className="h-10 rounded-2xl" data-testid="super-store-url-ios-customer"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">iOS Rider App Store URL</label>
              <Input value={form.ios_rider_store_url}
                     onChange={(e) => setForm({ ...form, ios_rider_store_url: e.target.value })}
                     placeholder="https://apps.apple.com/app/id..." className="h-10 rounded-2xl" data-testid="super-store-url-ios-rider"/>
            </div>
          </div>
          <Button onClick={saveNames} disabled={saving}
                  className="w-full h-11 rounded-2xl bg-brand-clay text-white font-bold"
                  data-testid="super-save-force-update">
            {saving ? "Saving…" : "Save minimum versions"}
          </Button>
        </div>

        <p className="text-sm text-neutral-600 mt-3">
          Google Play and the App Store require a <b>separate</b> Privacy Policy URL per app listing.
          Paste the matching link into each store entry below.
        </p>
        <div className="space-y-3 mt-2">
          {rows.map((r) => (
            <div key={r.key} className="rounded-2xl border border-neutral-200 p-3" data-testid={`legal-row-${r.key}`}>
              <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">{r.label}</div>
              <div className="flex items-center gap-2 mt-1.5">
                <code className="flex-1 text-xs font-mono break-all text-brand-ink" data-testid={`legal-url-${r.key}`}>{r.url}</code>
                <button onClick={() => copy(r.url, r.label)} className="h-9 w-9 rounded-xl bg-brand-cream border border-neutral-200 inline-flex items-center justify-center" data-testid={`copy-legal-${r.key}`}>
                  <Copy className="w-4 h-4" />
                </button>
                <a href={r.url} target="_blank" rel="noreferrer" className="h-9 px-3 rounded-xl bg-brand-sky text-white text-xs font-bold inline-flex items-center" data-testid={`open-legal-${r.key}`}>
                  Open
                </a>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

